import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Warning, FileXls, FileVideo, FilePdf, ShieldCheck, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const isPdf = (ct, name) => (ct || "").includes("pdf") || (name || "").toLowerCase().endsWith(".pdf");
const isExcel = (ct, name) => {
  const n = (name || "").toLowerCase();
  return (ct || "").includes("spreadsheet") || (ct || "").includes("excel") || n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv");
};
const isVideo = (ct, name) => {
  const n = (name || "").toLowerCase();
  return (ct || "").startsWith("video/") || n.endsWith(".mp4") || n.endsWith(".webm") || n.endsWith(".mov");
};

/**
 * Secure document viewer supporting PDF, Excel/CSV, and video.
 * All content served via authenticated blob URL. Watermark + no-download.
 */
export default function SecureViewer({ document: doc, open, onClose }) {
  const { user } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [excelSheet, setExcelSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [confidentialDisclaimer, setConfidentialDisclaimer] = useState(null);
  const videoRef = useRef(null);

  // Fetch the confidential disclaimer once — used for the compact banner
  useEffect(() => {
    if (!open) return;
    api.get("/disclaimers/confidential").then((r) => setConfidentialDisclaimer(r.data)).catch(() => setConfidentialDisclaimer(null));
  }, [open]);

  useEffect(() => {
    if (!open || !doc) return;
    let objectUrl;
    setLoading(true);
    setError(null);
    setExcelData(null);

    api.get(`/documents/${doc.id}/view`, { responseType: "blob" })
      .then(async (r) => {
        const blob = r.data;
        const ct = blob.type || doc.content_type;
        setContentType(ct);
        if (isExcel(ct, doc.name)) {
          const buf = await blob.arrayBuffer();
          const wb = XLSX.read(buf, { type: "array" });
          setExcelData(wb);
          setExcelSheet(0);
          setLoading(false);
        } else {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      })
      .catch(() => { setError("Unable to load document."); setLoading(false); });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBlobUrl(null);
      setContentType(null);
      setExcelData(null);
    };
  }, [open, doc?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c", "u"].includes(k)) e.preventDefault();
      if (k === "printscreen") e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Pause video on tab/window blur — anti screen-record deterrent
  useEffect(() => {
    if (!open) return;
    const pauseIfHidden = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden || !document.hasFocus()) v.pause();
    };
    document.addEventListener("visibilitychange", pauseIfHidden);
    window.addEventListener("blur", pauseIfHidden);
    return () => {
      document.removeEventListener("visibilitychange", pauseIfHidden);
      window.removeEventListener("blur", pauseIfHidden);
    };
  }, [open]);

  const watermarkText = `${user?.name || ""}\n${user?.email || ""}\n${formatDate(new Date().toISOString())}`;

  const kind = doc && (isVideo(contentType, doc.name) ? "video"
    : isExcel(contentType, doc.name) ? "excel"
    : isPdf(contentType, doc.name) ? "pdf" : "other");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden"
        onContextMenu={(e) => e.preventDefault()}
        data-testid="secure-viewer-modal"
      >
        <DialogTitle className="sr-only">{doc?.name || "Document"}</DialogTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-slate-900 text-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded bg-emerald-600 grid place-items-center shrink-0">
              {kind === "excel" ? <FileXls size={16} weight="fill" />
                : kind === "video" ? <FileVideo size={16} weight="fill" />
                : <FilePdf size={16} weight="fill" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{doc?.name}</div>
              <div className="text-[11px] text-white/60">Secure view · Watermarked for {user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-400 hidden sm:block font-semibold">Download disabled</div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="viewer-close-btn" className="text-white hover:bg-white/10 hover:text-white">
              <X size={18} />
            </Button>
          </div>
        </div>

        {/* Compact FCA disclaimer banner — collapsible */}
        {confidentialDisclaimer && (
          <div className="border-b border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30" data-testid="viewer-disclaimer-banner">
            <button
              onClick={() => setDisclaimerExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-amber-100/60 dark:hover:bg-amber-900/20 transition-colors"
              data-testid="viewer-disclaimer-toggle"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck size={14} weight="fill" className="text-amber-700 dark:text-amber-400 shrink-0" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-900 dark:text-amber-200 truncate">
                  Confidential Financial Promotion — Professional Clients Only
                </span>
              </div>
              <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 dark:text-amber-300 shrink-0">
                {disclaimerExpanded ? "Hide full disclaimer" : "View full disclaimer"}
                <CaretDown size={11} className={`transition-transform ${disclaimerExpanded ? "rotate-180" : ""}`} />
              </span>
            </button>
            {disclaimerExpanded && (
              <div className="px-4 pb-3 pt-1 text-[11px] text-amber-900/90 dark:text-amber-100/90 leading-relaxed whitespace-pre-line max-h-40 overflow-y-auto border-t border-amber-200/60 dark:border-amber-900/40" data-testid="viewer-disclaimer-body">
                {confidentialDisclaimer.body}
              </div>
            )}
          </div>
        )}

        <div
          className="secure-viewer relative flex-1 bg-slate-800 overflow-auto"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {loading && (
            <div className="absolute inset-0 grid place-items-center text-white/70">Loading secure document…</div>
          )}
          {error && (
            <div className="absolute inset-0 grid place-items-center text-red-300">{error}</div>
          )}

          {!loading && !error && kind === "pdf" && blobUrl && (
            <object
              data={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              type="application/pdf"
              className="h-full w-full"
              aria-label="Document viewer"
            >
              <div className="p-8 text-white/70">
                Your browser cannot display this PDF inline. This document cannot be downloaded.
              </div>
            </object>
          )}

          {!loading && !error && kind === "video" && blobUrl && (
            <div className="h-full w-full grid place-items-center bg-black relative">
              <video
                ref={videoRef}
                src={blobUrl}
                controls
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                className="max-h-full max-w-full"
                onContextMenu={(e) => e.preventDefault()}
              />
              {/* On-video watermark — burnt in overlay, moves subtly to deter cropping */}
              <div className="video-watermark pointer-events-none absolute inset-0 z-10">
                <div className="video-watermark-badge">
                  <div className="font-semibold">{user?.name}</div>
                  <div className="opacity-70">{user?.email}</div>
                  <div className="opacity-70 num-display">{formatDate(new Date().toISOString())}</div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && kind === "excel" && excelData && (
            <div className="p-6 bg-white dark:bg-slate-900 h-full overflow-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {excelData.SheetNames.map((sn, i) => (
                  <button
                    key={sn}
                    onClick={() => setExcelSheet(i)}
                    data-testid={`excel-sheet-${i}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      i === excelSheet
                        ? "bg-emerald-600 text-white"
                        : "bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {sn}
                  </button>
                ))}
              </div>
              <ExcelTable workbook={excelData} sheetIndex={excelSheet} />
            </div>
          )}

          {!loading && !error && kind === "other" && (
            <div className="absolute inset-0 grid place-items-center text-white/70">
              <div className="text-center">
                <Warning size={32} className="mx-auto mb-2" />
                <div>Preview not available for this file type.</div>
              </div>
            </div>
          )}

          <div className="watermark-overlay">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="watermark-tile">{watermarkText}</div>
            ))}
          </div>
        </div>

        <div className="px-4 py-2 border-t border-border bg-secondary/50 flex items-center justify-between text-[11px] text-muted-foreground">
          <div>All views are logged. Reproduction prohibited.</div>
          <div className="num-display">{doc?.folder?.replaceAll("_", " ")}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExcelTable({ workbook, sheetIndex }) {
  const sheet = workbook.Sheets[workbook.SheetNames[sheetIndex]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: "" });
  if (!rows.length) return <div className="text-sm text-muted-foreground italic">Empty sheet.</div>;
  const [header, ...body] = rows;
  const cols = Math.max(header.length, ...body.map((r) => r.length));
  return (
    <div className="overflow-auto rounded-lg border border-border">
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-20">
            <th className="px-2 py-2 w-10 text-center text-[10px] font-bold text-muted-foreground border-b border-r border-border sticky left-0 z-30 bg-slate-100 dark:bg-slate-800">#</th>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={i} className="px-3 py-2 text-left font-semibold border-b border-r border-border last:border-r-0 whitespace-nowrap">{String(header[i] ?? "")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => {
            const zebra = ri % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/70 dark:bg-slate-800/40";
            return (
              <tr key={ri} className={zebra}>
                <td className={`px-2 py-1.5 text-[10px] text-center text-muted-foreground border-b border-r border-border/70 sticky left-0 z-10 num-display ${zebra}`}>{ri + 1}</td>
                {Array.from({ length: cols }).map((_, ci) => (
                  <td key={ci} className="px-3 py-1.5 border-b border-r border-border/50 last:border-r-0 num-display whitespace-nowrap">{String(row[ci] ?? "")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
